import React, { useEffect, useState } from "react";
import PageTitle from "../../Common/PageTitle"
import {
  Box, Container, Grid, GridItem, Heading, Text, Image, HStack, VStack,
  Divider, Wrap, WrapItem, Link, Input, useColorModeValue,
  Accordion, AccordionItem, AccordionButton, AccordionIcon, AccordionPanel, Center
} from '@chakra-ui/react';
import { FaTwitter, FaLinkedin, FaFacebook } from 'react-icons/fa';
import { FaWhatsapp } from "react-icons/fa6";
import { FiCalendar } from 'react-icons/fi';
import { useParams } from "react-router-dom";
import moment from "moment"
import Seo, { SITE_URL } from '../../Common/Seo';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { getBlogBySlug } from './staticBlogs';

const BlogDetail = () => {
  const params = useParams();
  const blogData = getBlogBySlug(params.name);
  const [finalHTML, setFinalHTML] = useState("");

  useEffect(() => {
    if (!blogData?.long_des) {
      setFinalHTML("");
      return;
    }

    const clean = sanitizeHtml(blogData.long_des);
    if (!blogData?.toc?.length) {
      setFinalHTML(clean);
      return;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(clean, "text/html");
    const headings = doc.querySelectorAll("h1, h2, h3, h4, h5, h6");

    blogData.toc.forEach((item) => {
      const match = Array.from(headings).find((h) =>
        h.innerText.trim().includes(item.text.replace(/^\d+\.\s*/, "").trim())
      );
      if (match) {
        match.id = item.anchor;
      }
    });

    setFinalHTML(sanitizeHtml(doc.body.innerHTML));
  }, [blogData]);

  const bg = useColorModeValue('white', 'gray.800');
  const muted = useColorModeValue('gray.600', 'gray.400');

  const slug = blogData?.slug || blogData?.url_text || params.name;
  const canonicalPath = `/blogs/${slug}`;
  const pageDescription =
    blogData?.meta_description || blogData?.short_des || "OneBigBit technology blog.";

  if (!blogData) {
    return (
      <>
        <Seo title="Blog Not Found" path={`/blogs/${params.name || ""}`} noindex description="The requested blog post could not be found." />
        <Center minH="50vh" flexDirection="column" gap={3}>
          <Heading size="md">Blog post not found</Heading>
          <Text color="gray.600">The article you are looking for may have been moved or removed.</Text>
          <Link href="/blogs" color="blue.500">Back to blogs</Link>
        </Center>
      </>
    );
  }

  return (
    <>
      <Seo
        title={blogData.meta_title || blogData.title}
        description={pageDescription}
        path={blogData.canonical_url?.includes("onebigbit.com") ? blogData.canonical_url : canonicalPath}
        image={blogData.og_image || blogData.image_url}
        type={blogData.og_type || "article"}
        keywords={Array.isArray(blogData.keywords) ? blogData.keywords.join(", ") : blogData.keywords}
        jsonLd={blogData.schema_markup}
      />
      <div className="slidersection_here ">
        <div className="pageheader bg-blue"></div>
        <PageTitle
          title={blogData.title}
          prevtitle="blogs"
          desc={blogData.short_des}
          bimg={blogData.image_url || "/favicon.png"}
          page={blogData.title}
          skipSeo
        />
        <section>
          <div className="container about_page" style={{ marginBottom: "-120px" }}>
            <Box bg={'gray.50'} py={{ base: 6, md: 12 }} className="mt-5">
              <Container maxW="7xl">
                <Grid
                  templateColumns={{ base: '1fr', md: '3fr 1fr' }}
                  gap={{ base: 6, md: 10 }}
                >
                  <GridItem>
                    <Box bg={bg} rounded="xl" overflow="hidden" shadow="sm">
                      <Image
                        src={blogData.image_url}
                        alt={blogData.title}
                        objectFit="cover"
                        w="100%"
                        h={{ base: '200px', md: '380px' }}
                      />

                      <Box p={{ base: 4, md: 8 }}>
                        <Heading as="h3" size={{ base: "md", md: "lg" }} mb={2}>
                          {blogData.title}
                        </Heading>
                        <Divider my={3} />
                        {blogData?.toc?.length > 0 && (
                          <Box bg={bg} p={4} rounded="lg" shadow="sm">
                            <Heading size="sm" mb={3}>Table of Content</Heading>
                            <VStack align={'stretch'} spacing={2} maxH={'300px'} p={10} background={'gray.100'} overflowY={'auto'}>
                              {blogData.toc.map((data, idx) => (
                                <Box
                                  key={idx}
                                  cursor={'pointer'}
                                  fontSize={'sm'}
                                  color={'gray.700'}
                                  py={0} fontWeight={'700'}
                                  _hover={{ color: "blue.600" }}
                                  onClick={() => {
                                    const el = document.getElementById(data.anchor);
                                    el?.scrollIntoView({ behavior: "smooth", block: "start" });
                                  }}
                                >
                                  {data.text}
                                </Box>
                              ))}
                            </VStack>
                          </Box>
                        )}
                        <Box color={muted} fontSize="md" mt={3}>
                          <div dangerouslySetInnerHTML={{ __html: finalHTML }} />
                        </Box>

                        <Divider my={6} />

                        {blogData.faq && blogData.faq.length > 0 && (
                          <Box mt={8}>
                            <Heading size="lg" mb={4}>FAQs</Heading>
                            <Accordion allowToggle>
                              {blogData.faq.map((item, index) => (
                                <AccordionItem key={index} border="1px solid" borderColor="gray.200" rounded="md" mb={3}>
                                  <h2>
                                    <AccordionButton py={3}>
                                      <Box flex="1" textAlign="left" fontWeight="bold">
                                        {item.question}
                                      </Box>
                                      <AccordionIcon />
                                    </AccordionButton>
                                  </h2>
                                  <AccordionPanel pb={4} color="gray.600">
                                    {item.answer}
                                  </AccordionPanel>
                                </AccordionItem>
                              ))}
                            </Accordion>
                          </Box>
                        )}

                        <HStack mt={5} spacing={4} wrap="wrap" display={'flex'} justifyContent={'space-between'}>
                          <Text display={'flex'} fontSize="sm">
                            <FiCalendar /> &nbsp;
                            {moment(blogData.createdAt || blogData.updatedAt).format('DD MMM YYYY')}
                          </Text>

                          <Text display={'flex'} gap={3} fontSize="sm" textTransform={'capitalize'}>
                            <Link
                              href={blogData?.share_links?.facebook || `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`${SITE_URL}${canonicalPath}`)}`}
                              isExternal
                              aria-label="Share on Facebook"
                              rel="noopener noreferrer"
                            >
                              <FaFacebook className="fs-5" />
                            </Link>
                            <Link
                              href={blogData?.share_links?.whatsapp || `https://api.whatsapp.com/send?text=${encodeURIComponent(`${blogData.title} ${SITE_URL}${canonicalPath}`)}`}
                              isExternal
                              aria-label="Share on WhatsApp"
                              rel="noopener noreferrer"
                            >
                              <FaWhatsapp className="fs-5" />
                            </Link>
                            <Link
                              href={blogData?.share_links?.linkedin || `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`${SITE_URL}${canonicalPath}`)}`}
                              isExternal
                              aria-label="Share on LinkedIn"
                              rel="noopener noreferrer"
                            >
                              <FaLinkedin className="fs-5" />
                            </Link>
                            <Link
                              href={blogData?.share_links?.twitter || `https://twitter.com/intent/tweet?url=${encodeURIComponent(`${SITE_URL}${canonicalPath}`)}&text=${encodeURIComponent(blogData.title)}`}
                              isExternal
                              aria-label="Share on Twitter"
                              rel="noopener noreferrer"
                            >
                              <FaTwitter className="fs-5" />
                            </Link>
                          </Text>
                        </HStack>
                      </Box>
                    </Box>
                  </GridItem>

                  <GridItem>
                    <VStack align="stretch" spacing={6}>
                      <Box bg={bg} p={4} rounded="lg" shadow="sm">
                        <Heading size="sm" mb={3}>Quick Search</Heading>
                        <Input placeholder="Search Here!" aria-label="Search blogs" />
                      </Box>
                      <Box bg={bg} p={4} rounded="lg" shadow="sm">
                        <Heading size="sm" mb={3}>Tags</Heading>
                        <Wrap spacing={2}>
                          {blogData.tags && blogData.tags.length > 0 ? (
                            blogData.tags.map((tag, index) => (
                              <WrapItem key={index}>
                                <Box
                                  px={3}
                                  py={1}
                                  fontSize="sm"
                                  bg="gray.200"
                                  color="gray.700"
                                  rounded="full"
                                  textTransform="capitalize"
                                >
                                  {tag}
                                </Box>
                              </WrapItem>
                            ))
                          ) : (
                            <Text fontSize="sm" color="gray.500">No tags available</Text>
                          )}
                        </Wrap>
                      </Box>
                    </VStack>
                  </GridItem>
                </Grid>
              </Container>
            </Box>
          </div>
        </section>
      </div>
    </>
  )
}

export default BlogDetail
